import { RocheEndpoint } from '@repo/roche-xx-shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { SearchTrackingType, trackSearchEvent } from '../tracking';
import { fetchSearchResults, RocheSearchResultItem, SearchCategory } from './queries';

type UseSearchCategoryProps = {
  searchQuery: string;
  categoryFilter: SearchCategory;
  page: number;
  searchTrigger?: SearchTrackingType;
  shouldTrack?: boolean;
};

const searchDebounce = 300;

export const useSearch = ({
  searchQuery,
  categoryFilter,
  page,
  searchTrigger = SearchTrackingType.MAIN_SEARCH,
  shouldTrack = false,
}: UseSearchCategoryProps) => {
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const trackedQueryRef = useRef<string>('');

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    searchDebounce,
    [searchQuery],
  );

  const query = useQuery<RocheEndpoint<RocheSearchResultItem[]>, Error>({
    queryKey: ['searchCategory', debouncedSearchQuery, page, categoryFilter],
    queryFn: () =>
      fetchSearchResults({
        searchQuery: debouncedSearchQuery,
        page,
        categoryFilter,
      }),
    enabled: !!debouncedSearchQuery && debouncedSearchQuery.length > 0,
  });

  useEffect(() => {
    const queryKey = `${debouncedSearchQuery}-${categoryFilter}-${page}`;

    if (
      shouldTrack &&
      debouncedSearchQuery &&
      debouncedSearchQuery.length > 0 &&
      query.isSuccess &&
      query.data &&
      trackedQueryRef.current !== queryKey
    ) {
      trackedQueryRef.current = queryKey;

      trackSearchEvent(
        {
          term: debouncedSearchQuery,
          results: query.data.Total || 0,
          filter: {
            pagination: page,
            filter: categoryFilter,
          },
        },
        searchTrigger,
      );
    }
  }, [shouldTrack, debouncedSearchQuery, categoryFilter, page, query.isSuccess, query.data]);

  return query;
};
